import { memo, useCallback } from 'react';
import { Avatar, Badge, Button, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { ROLE_KIND_META } from '../../constants/copy';
import { ROLE_TYPES } from '../../constants/roles';
import { PERMISSIONS } from '../../constants/permissions';
import { getAvatarColorClass } from '../../utils/avatarColor';
import { getInitials } from '../../utils/memberDisplay';
import Can from '../guards/Can';

/**
 * One role of the Roles list, as in the prototype: name with its type badge, a two-line
 * description, and a footer with the first members' avatars, the member count and Edit role
 * (custom, needs the manage permission) or View role. A click anywhere on the card opens the role
 * (a mouse shortcut); the View / Edit button is the keyboard and screen reader control, so the card
 * itself is not a `button` (no interactive element nested in another).
 *
 * @param {object} props
 * @param {object} props.role UI role (see `mapApiRoleToUi`)
 * @param {(roleId: string) => void} props.onOpen
 */
const RoleCard = ({ role, onOpen }) => {
  const { t } = useTranslation('people-access');
  const { id, name, description, isSystem, memberCount, membersPreview } = role;
  const kindMeta = ROLE_KIND_META[isSystem ? ROLE_TYPES.SYSTEM : ROLE_TYPES.CUSTOM];

  const handleOpen = useCallback(() => onOpen(id), [onOpen, id]);
  const handleAction = useCallback(
    (event) => {
      event.stopPropagation();
      onOpen(id);
    },
    [onOpen, id],
  );

  const view = (
    <Button variant="secondary" size="sm" aria-label={t('roles.list.viewRoleAria', { name })} onClick={handleAction}>
      {t('roles.list.viewRole')}
    </Button>
  );

  return (
    <Card
      data-testid={`role-card-${name}`}
      onClick={handleOpen}
      className="flex cursor-pointer flex-col gap-3 p-4 transition-shadow hover:shadow-md"
    >
      <div className="flex min-w-0 items-center gap-2">
        <h3 className="truncate text-sm font-semibold text-content">{name}</h3>
        <Badge size="small" variant={kindMeta.variant}>
          {t(kindMeta.labelKey)}
        </Badge>
      </div>
      <p className="line-clamp-2 min-h-[2.5rem] text-xs text-content-secondary">{description}</p>
      <div className="mt-auto flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {membersPreview.length ? (
            <div className="flex shrink-0 -space-x-1.5" aria-hidden>
              {membersPreview.map((member) => (
                <Avatar
                  key={member.id}
                  size="md"
                  initials={getInitials(member.name)}
                  className={`${getAvatarColorClass(member.name)} text-[9px] ring-2 ring-surface`}
                />
              ))}
            </div>
          ) : null}
          <span className="truncate text-xs text-content-secondary">
            {t('roles.list.memberCount', { count: memberCount })}
          </span>
        </div>
        {isSystem ? (
          view
        ) : (
          <Can permission={PERMISSIONS.ROLES_MANAGE} fallback={view}>
            <Button variant="secondary" size="sm" aria-label={t('roles.list.editRoleAria', { name })} onClick={handleAction}>
              {t('roles.list.editRole')}
            </Button>
          </Can>
        )}
      </div>
    </Card>
  );
};

export default memo(RoleCard);
